'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useNotification } from '@umodeapporg/ui/hooks/useNotification';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { corDaMarca, corPorMarca } from '@/app/mapa/helpers/formatMapa';
import { formatCurrency, formatNumber } from '@/app/ops/helpers/formatters';
import { opsEmRisco } from '@/app/ops/helpers/opsRisco';
import { formatDiaCurto, formatDiaLongo } from '@/app/relatorios/helpers/formatRelatorios';
import { useContaAtiva } from '@/domain/auth/hooks/useContaAtiva';
import { IClassesCorMarca } from '@/domain/cadastros/helpers/paletaMarcas';
import { useUrlQueryState } from '@/domain/common/hooks/useUrlQueryState';
import { calendarioDaFabrica } from '@/domain/configuracao/helpers/calendarioFabrica';
import { faixasDeOcupacao } from '@/domain/configuracao/helpers/faixasOcupacao';
import { hojeNaFabricaISO } from '@/domain/configuracao/helpers/hojeNaFabricaISO';
import { useConfiguracao } from '@/domain/configuracao/hooks/useConfiguracao';
import { useMapa } from '@/domain/mapa/hooks/useMapa';
import { cortesDaGravidade } from '@/domain/ops/helpers/gravidadeDoFarol';
import { useOps } from '@/domain/ops/hooks/useOps';
import { useIndicadores } from '@/domain/relatorios/hooks/useRelatorios';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';

import { CapacidadeBloco } from './components/CapacidadeBloco';
import { MarcasBloco } from './components/MarcasBloco';
import { OciosidadeBloco } from './components/OciosidadeBloco';
import { PeriodoSeletor } from './components/PeriodoSeletor';
import { PontualidadeBloco } from './components/PontualidadeBloco';
import { ProducaoBloco } from './components/ProducaoBloco';
import { RiscoBloco } from './components/RiscoBloco';
import { ocupacaoPorMarca, TMedidaDaProducao } from './helpers/ocupacaoPorMarca';
import { IPeriodoDaVisao, periodoDaUrl, periodoParaUrl, semanasDoPeriodo } from './helpers/periodoDaVisao';
import { visaoExecutiva } from './helpers/visaoExecutiva';
import { exportVisaoExecutivaToXlsx } from './helpers/visaoExecutivaExport';
import { CARTEIRA_EM_RISCO_HREF } from './helpers/visaoExecutivaLinks';

/**
 * As larguras dos blocos, fileira por fileira. A tela é um relatório de UMA página: em
 * 1366 x 768 as fileiras cabem sem rolar sempre que dá, e abaixo de `laptop` os blocos
 * empilham na mesma ordem das perguntas.
 *
 * A ordem é a aprovada: o dinheiro em risco abre a conversa, as duas leituras BAIXAS (a
 * capacidade e a pontualidade) vêm no meio, e as duas ALTAS (quem ocupa a produção e a
 * performance por cliente) fecham a tela lado a lado, na mesma altura.
 */
const BLOCO_RISCO = 'min-w-0 laptop:basis-[calc(40%_-_0.5rem)]';
const BLOCO_OCIOSIDADE = 'min-w-0 laptop:basis-[calc(60%_-_0.5rem)]';
const BLOCO_CAPACIDADE = 'min-w-0 laptop:basis-[calc(60%_-_0.5rem)]';
const BLOCO_PONTUALIDADE = 'min-w-0 laptop:basis-[calc(40%_-_0.5rem)]';
const BLOCO_PRODUCAO = 'min-w-0 laptop:basis-[calc(50%_-_0.5rem)]';
const BLOCO_MARCAS = 'min-w-0 laptop:basis-[calc(50%_-_0.5rem)]';

/** A chave do período na barra de endereço: o recorte cabe num link. */
const PERIODO_PARAM = 'periodo';

/**
 * As cinco perguntas que um dono ou diretor faz, numa página só.
 *
 * Mora num componente, e não numa rota: a Visão executiva é uma ABA do Início, e quem põe
 * a moldura e o título é a tela de lá. A rota antiga `/visao-executiva` continua de pé e
 * manda para a aba, para não quebrar link já enviado.
 *
 * A tela não tem endpoint próprio e não calcula nada de novo: ela lê a carteira inteira
 * (`loadCarteira`) e a grade da fábrica (`loadGradeDaFabrica`) e agrupa com as MESMAS
 * funções da Carteira e do Mapa, para os números daqui nunca discordarem dos de lá. Cada
 * bloco mostra o seu carregando, o seu erro e o seu vazio: a carteira e a grade são duas
 * fontes, e quando uma cai o resto da página continua valendo.
 */
export function VisaoExecutivaPanel() {
  const { t } = useTranslation('common');
  const { notify } = useNotification();
  const { carteira, carteiraLoading, carteiraFailed, loadCarteira } = useOps();
  const {
    gradeDaFabrica,
    gradeDaFabricaLoading,
    gradeDaFabricaFailed,
    loadGradeDaFabrica,
    marcasDaFabrica,
    marcasDaFabricaLoading,
    loadMarcasDaFabrica,
  } = useMapa();
  // Os cortes de cor da ocupação são parâmetro (Configurações › Ocupação): a mini grade
  // daqui pinta pelos mesmos dois números que a grade do Mapa.
  const { config, load: loadConfig } = useConfiguracao();
  /** The active account: it names the Excel and opens its first row. */
  const conta = useContaAtiva();
  /** A pontualidade REALIZADA sai do mesmo cálculo dos Relatórios, nunca de outra conta. */
  const { indicadores, load: loadIndicadores } = useIndicadores();
  const { getParam, setParams } = useUrlQueryState();

  /** O período vive no link, então um recorte pode ser mandado para alguém. */
  const periodo = periodoDaUrl(getParam(PERIODO_PARAM));
  const [medida, setMedida] = useState<TMedidaDaProducao>('HORAS');

  const handlePeriodo = useCallback(
    (novo: IPeriodoDaVisao): void => {
      setParams({ [PERIODO_PARAM]: periodoParaUrl(novo) });
    },
    [setParams],
  );

  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    const run = async (): Promise<void> => {
      await Promise.all([loadCarteira(), loadGradeDaFabrica(), loadConfig(), loadMarcasDaFabrica(), loadIndicadores()]);
    };
    run();
  }, [loadCarteira, loadConfig, loadGradeDaFabrica, loadIndicadores, loadMarcasDaFabrica]);

  const handleRetryCarteira = useCallback((): void => {
    const run = async (): Promise<void> => {
      await loadCarteira();
    };
    run();
  }, [loadCarteira]);

  const handleRetryGrade = useCallback((): void => {
    const run = async (): Promise<void> => {
      await loadGradeDaFabrica();
    };
    run();
  }, [loadGradeDaFabrica]);

  /**
   * Os blocos por célula leem as duas fontes, então o "tentar de novo" pede as duas, com
   * as cores das marcas junto: se só elas tiverem falhado, a pizza volta colorida.
   */
  const handleRetryTudo = useCallback((): void => {
    const run = async (): Promise<void> => {
      await Promise.all([loadCarteira(), loadGradeDaFabrica(), loadMarcasDaFabrica()]);
    };
    run();
  }, [loadCarteira, loadGradeDaFabrica, loadMarcasDaFabrica]);

  /** Hoje no fuso da FÁBRICA, o mesmo dia que a Carteira usa para dizer o que venceu. */
  const hoje = hojeNaFabricaISO();
  const faixas = useMemo(() => faixasDeOcupacao(config?.ocupacao), [config]);
  // Os dois cortes que graduam o vermelho (Configurações › Prazo): o bloco do risco abre
  // o total em graus, e a cor de nenhuma OP muda por causa deles.
  const cortes = useMemo(() => cortesDaGravidade(config?.prazo), [config]);
  // O mesmo calendário do motor (jornada e feriados ativos): é ele que diz a partir de
  // que dia a célula fica livre, pulando fim de semana e feriado.
  const calendario = useMemo(() => calendarioDaFabrica(config?.jornada, config?.feriados), [config]);

  /** As semanas do período: a capacidade e a pizza falam das MESMAS, nunca de duas janelas. */
  const semanasVisiveis = useMemo(
    () => semanasDoPeriodo(gradeDaFabrica?.semanas ?? [], periodo),
    [gradeDaFabrica, periodo],
  );

  /** The window on screen, which "De / até" opens on; before the grid arrives, today alone. */
  const intervaloVisivel = useMemo(
    () => ({
      de: semanasVisiveis[0]?.inicio ?? hoje,
      ate: semanasVisiveis[semanasVisiveis.length - 1]?.fim ?? hoje,
    }),
    [hoje, semanasVisiveis],
  );

  /**
   * A cor de cada marca, pela MESMA leitura que pinta o Mapa: a lista de marcas da grade,
   * onde o servidor já resolveu a cor do cadastro ou a provisória de quem não escolheu
   * nenhuma. Ler o `corMapa` do cadastro aqui era o que deixava a pizza inteira cinza numa
   * fábrica onde ninguém abriu "Gravar cores sugeridas": todas as marcas caíam na classe
   * neutra (`fill-muted-active`), enquanto o Mapa as pintava normalmente.
   */
  const cores = useMemo(() => corPorMarca(marcasDaFabrica), [marcasDaFabrica]);
  const coresDaMarca = useCallback((marca: string): IClassesCorMarca => corDaMarca(marca, cores), [cores]);

  const producao = useMemo(
    () =>
      ocupacaoPorMarca({
        quadros: gradeDaFabrica?.quadros ?? [],
        semanas: semanasVisiveis,
        ops: carteira ?? [],
        medida,
      }),
    [carteira, gradeDaFabrica, medida, semanasVisiveis],
  );

  /** O período escrito, para o subtítulo dizer de que janela são aqueles números. */
  const periodoTexto =
    periodo.atalho === null
      ? t('visaoExecutiva.periodo.intervalo', {
          de: formatDiaCurto(periodo.de),
          ate: formatDiaCurto(periodo.ate),
        })
      : t(`visaoExecutiva.periodo.escrito.${periodo.atalho}`);

  const handleExportar = async (): Promise<void> => {
    setExportando(true);
    try {
      await exportVisaoExecutivaToXlsx({
        fatias: producao.fatias,
        periodoTexto,
        t,
        today: new Date(),
        conta: conta.nome,
      });
      notify({ message: t('visaoExecutiva.exportar.sucesso', { n: producao.fatias.length }), type: 'success' });
    } catch (error) {
      logger.error('[VisaoExecutivaPanel] export failed', error);
      notify({ message: t('visaoExecutiva.exportar.erro'), type: 'error' });
    } finally {
      setExportando(false);
    }
  };

  const visao = useMemo(() => {
    const ops = carteira ?? [];
    return visaoExecutiva({
      ops,
      emRisco: opsEmRisco(ops, hoje),
      grade: gradeDaFabrica,
      hoje,
      semanasVisiveis,
      faixas,
      calendario,
      cortes,
      // A jornada diz quando o expediente do dia acaba: é ela que separa "a célula
      // fica livre às 14:30 de hoje" de "só amanhã".
      jornada: config?.jornada,
    });
  }, [calendario, carteira, config, cortes, faixas, gradeDaFabrica, hoje, semanasVisiveis]);

  // Nada carregado e nenhuma falha registrada quer dizer que a primeira leitura está vindo.
  const carteiraCarregando = carteiraLoading || (carteira === null && !carteiraFailed);
  const gradeCarregando = gradeDaFabricaLoading || (gradeDaFabrica === null && !gradeDaFabricaFailed);

  /** A carteira em uma linha, só depois de lida: zeros no cabeçalho seriam uma notícia falsa. */
  const headline =
    carteiraCarregando || carteiraFailed
      ? formatDiaLongo(hoje)
      : t('visaoExecutiva.subtitle', {
          data: formatDiaLongo(hoje),
          ops: formatNumber(visao.risco.opsCarteira),
          faturamento: formatCurrency(visao.risco.faturamentoCarteira),
        });

  return (
    // Sem moldura de página e sem PageTitle: quem põe os dois é o Início, a tela dona desta
    // aba. O aperto para caber em 1366 x 768 continua vindo das fileiras e dos cartões.
    <div className="flex w-full flex-col gap-y-5">
      {/* A carteira em uma linha. Era o subtítulo quando a tela tinha rota própria, e segue
          aqui porque é ele que diz de QUANTO se está falando. */}
      <p className="text-sm text-foreground-muted">{headline}</p>

      <div className="flex flex-col gap-3 laptop:flex-row">
        <RiscoBloco
          className={BLOCO_RISCO}
          risco={visao.risco}
          href={CARTEIRA_EM_RISCO_HREF}
          loading={carteiraCarregando}
          failed={carteiraFailed}
          onRetry={handleRetryCarteira}
        />
        <OciosidadeBloco
          className={BLOCO_OCIOSIDADE}
          celulas={visao.celulas}
          loading={gradeCarregando || carteiraCarregando}
          failed={gradeDaFabricaFailed || carteiraFailed}
          onRetry={handleRetryTudo}
        />
      </div>

      {/* O período manda na capacidade e em "quem ocupa", que agora estão em fileiras
          diferentes: ele fica acima das duas e o rótulo dele diz de quem ele é, porque
          dentro de um dos cartões pareceria filtro só daquele. */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <PeriodoSeletor periodo={periodo} intervaloVisivel={intervaloVisivel} onChange={handlePeriodo} />

          {/* A planilha é a de "quem ocupa a produção", então ela acompanha o período. */}
          <ControlledButton
            variant="outlined"
            tone="neutral"
            size="sm"
            className="normal-case"
            disabled={exportando || producao.fatias.length === 0}
            onClick={handleExportar}
          >
            <MaterialSymbol icon="download" size={18} aria-hidden />
            {t('visaoExecutiva.exportar.acao')}
          </ControlledButton>
        </div>

        {/* A fileira BAIXA: duas listas curtas por célula, uma linha por célula. */}
        <div className="flex flex-col gap-3 laptop:flex-row">
          <CapacidadeBloco
            className={BLOCO_CAPACIDADE}
            celulas={visao.celulas}
            faixas={faixas}
            loading={gradeCarregando}
            failed={gradeDaFabricaFailed}
            onRetry={handleRetryGrade}
          />
          <PontualidadeBloco
            className={BLOCO_PONTUALIDADE}
            celulas={visao.celulas}
            realizada={indicadores?.erroPromessa ?? null}
            loading={gradeCarregando || carteiraCarregando}
            failed={gradeDaFabricaFailed || carteiraFailed}
            onRetry={handleRetryTudo}
          />
        </div>

        {/* A fileira ALTA: a pizza e a lista de marcas, que pedem altura e a dividem
            igual. Como são irmãs na mesma linha, elas esticam na MESMA altura. */}
        <div className="flex flex-col gap-3 laptop:flex-row">
          <ProducaoBloco
            className={BLOCO_PRODUCAO}
            leitura={producao}
            medida={medida}
            onMedida={setMedida}
            periodoTexto={periodoTexto}
            coresDaMarca={coresDaMarca}
            // As cores entram na espera do bloco: a pizza cinza que recolore depois era
            // justamente o que a tela mostrava de errado no staging.
            loading={gradeCarregando || carteiraCarregando || marcasDaFabricaLoading}
            failed={gradeDaFabricaFailed || carteiraFailed}
            onRetry={handleRetryTudo}
          />
          <MarcasBloco
            className={BLOCO_MARCAS}
            marcas={visao.marcas}
            loading={carteiraCarregando}
            failed={carteiraFailed}
            onRetry={handleRetryCarteira}
          />
        </div>
      </div>
    </div>
  );
}
